/* Inprint: matches selected card options to a variant, updates hidden id, button price and state,
   and swaps the card image to the selected variant's image (e.g. frame colour).
   On load the card keeps the product's main image and preselects the smallest available size in
   the frame shown on that image, so picture and selects agree. The picture only changes when the
   customer changes an option. If the chosen variant has no image of its own (smaller framed-poster
   sizes), the image of a variant with the same frame is shown (the largest size has one per frame). */
if (!customElements.get('card-quick-pick')) {
  customElements.define(
    'card-quick-pick',
    class CardQuickPick extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('[data-card-variants]');
        if (!data) return;
        try {
          this.variants = JSON.parse(data.textContent);
        } catch (e) {
          return;
        }
        this.form = this.closest('form');
        this.input = this.form ? this.form.querySelector('input[name="id"]') : null;
        this.button = this.querySelector('button[type="submit"]');
        this.label = this.querySelector('.card-quick-pick__label');
        this.text = this.querySelector('[data-cqp-text]');
        this.price = this.querySelector('[data-cqp-price]');
        this.selects = Array.from(this.querySelectorAll('select[data-option-index]'));
        if (!this.input || !this.button) return;

        const wrapper = this.closest('.card-wrapper');
        this.img = wrapper ? wrapper.querySelector('.card__media img') : null;
        if (this.img) {
          this.original = {
            media: this.img.getAttribute('data-cqp-media') || '',
            src: this.img.getAttribute('src') || '',
            srcset: this.img.getAttribute('srcset') || '',
          };
        }

        this.selects.forEach((select) => select.addEventListener('change', () => this.update()));
        this.preselectFromImage();
      }

      /* Select the smallest available size in the frame that the main image shows */
      preselectFromImage() {
        if (!this.original || !this.original.media || this.selects.length < 2) return;
        const shown = this.variants.find((v) => String(v.media) === this.original.media);
        if (!shown) return;
        const sameFrame = this.variants.filter((v) =>
          v.options.every((value, k) => k === 0 || value === shown.options[k])
        );
        const pick = sameFrame.find((v) => v.available) || shown;
        const current = this.currentVariant();
        if (current && current.id === pick.id) return;
        this.selects.forEach((select, i) => {
          if (pick.options[i] !== undefined) select.value = pick.options[i];
        });
        this.update(true);
      }

      currentVariant() {
        const chosen = this.selects.map((select) => select.value);
        return this.variants.find((v) => v.options.every((value, i) => value === chosen[i]));
      }

      /* Closest variant that has an image: all options but one must match; a match on a later
         option (frame) weighs more than on an earlier one (size). */
      imageVariant(variant) {
        if (!variant) return null;
        if (variant.media && variant.src) return variant;
        let best = null;
        let bestScore = -1;
        this.variants.forEach((v) => {
          if (!v.media || !v.src || v === variant) return;
          let matches = 0;
          let score = 0;
          v.options.forEach((value, k) => {
            if (value === variant.options[k]) {
              matches += 1;
              score += k + 1;
            }
          });
          if (matches >= variant.options.length - 1 && score > bestScore) {
            best = v;
            bestScore = score;
          }
        });
        return best;
      }

      syncImage(variant) {
        if (!this.img || !this.original) return;
        const source = this.imageVariant(variant);
        const target = source
          ? { media: String(source.media), src: source.src, srcset: source.srcset || '' }
          : this.original;
        if (this.img.getAttribute('data-cqp-media') === target.media) return;
        this.img.setAttribute('data-cqp-media', target.media);
        if (target.srcset) {
          this.img.setAttribute('srcset', target.srcset);
        } else {
          this.img.removeAttribute('srcset');
        }
        this.img.setAttribute('src', target.src);
      }

      update(keepImage) {
        const variant = this.currentVariant();
        if (!keepImage) this.syncImage(variant);

        const soldOutMessage = this.button.querySelector('.sold-out-message');
        if (soldOutMessage) soldOutMessage.classList.add('hidden');
        if (this.label) this.label.classList.remove('hidden');
        this.button.removeAttribute('aria-disabled');

        if (!variant) {
          this.input.disabled = true;
          this.button.disabled = true;
          this.text.textContent = this.dataset.unavailable;
          this.price.textContent = '';
          return;
        }

        this.input.value = variant.id;

        if (!variant.available) {
          this.input.disabled = true;
          this.button.disabled = true;
          this.text.textContent = this.dataset.soldOut;
          this.price.textContent = '';
          return;
        }

        this.input.disabled = false;
        this.button.disabled = false;
        this.text.textContent = this.dataset.addToCart;
        this.price.textContent = variant.price;
      }
    }
  );
}
